import type { UseStore } from 'idb-keyval';

/**
 * Shared IndexedDB opener for all portal-query storage modules.
 *
 * idb-keyval's `createStore` cannot host multiple object stores in one
 * database: it opens without a version, so `onupgradeneeded` only fires when
 * the database is first created — by whichever module wins the race. Every
 * other module's store is then missing and all its transactions throw
 * `NotFoundError` (jsw-main#258).
 *
 * `createPortalStore` fixes this by funnelling every open of a given database
 * through one cached connection, and bumping the version to create any object
 * store that does not exist yet. Existing data is preserved across upgrades.
 */

const connections = new Map<string, Promise<IDBDatabase>>();

function promisifyRequest<T = undefined>(request: IDBRequest<T> | IDBOpenDBRequest): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        request.onsuccess = () => resolve((request as IDBRequest<T>).result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Closes the connection when another tab (or a later store request in this
 * tab) needs a version upgrade, so the upgrade is not blocked forever.
 */
function watchConnection(dbName: string, db: IDBDatabase): IDBDatabase {
    db.onversionchange = () => {
        db.close();
        connections.delete(dbName);
    };
    db.onclose = () => {
        connections.delete(dbName);
    };
    return db;
}

function openInitial(dbName: string, storeName: string): Promise<IDBDatabase> {
    const request = indexedDB.open(dbName);
    request.onupgradeneeded = () => {
        request.result.createObjectStore(storeName);
    };
    return promisifyRequest(request).then((db) => watchConnection(dbName, db));
}

function upgradeWithStore(dbName: string, db: IDBDatabase, storeName: string): Promise<IDBDatabase> {
    const version = db.version + 1;
    db.close();
    const request = indexedDB.open(dbName, version);
    request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(storeName)) {
            request.result.createObjectStore(storeName);
        }
    };
    return promisifyRequest(request).then((upgraded) => watchConnection(dbName, upgraded));
}

/**
 * Resolves a live connection whose database contains `storeName`, creating
 * the store through a version bump when missing. Requests are chained per
 * database so concurrent callers (offline-cache, mutation-queue, query-cache,
 * blob-store) serialize their upgrades instead of racing.
 */
function getConnection(dbName: string, storeName: string): Promise<IDBDatabase> {
    const current = connections.get(dbName) ?? openInitial(dbName, storeName);

    const next = current.then((db) => {
        if (db.objectStoreNames.contains(storeName)) return db;
        return upgradeWithStore(dbName, db, storeName);
    });

    connections.set(
        dbName,
        next.catch(() => {
            // Don't poison the cache: let the next caller retry a fresh open.
            connections.delete(dbName);
            return openInitial(dbName, storeName);
        }),
    );

    return next;
}

/**
 * Drop-in replacement for idb-keyval's `createStore` that supports multiple
 * object stores in one database. The returned `UseStore` is compatible with
 * idb-keyval's `get`/`set`/`del`/`keys` helpers.
 */
export function createPortalStore(dbName: string, storeName: string): UseStore {
    return (txMode, callback) =>
        getConnection(dbName, storeName).then((db) => {
            try {
                return callback(db.transaction(storeName, txMode).objectStore(storeName));
            } catch (error) {
                // The cached connection may have been closed (versionchange from
                // another tab, browser eviction). Reopen once and retry.
                if (error instanceof DOMException && error.name === 'InvalidStateError') {
                    connections.delete(dbName);
                    return getConnection(dbName, storeName).then((fresh) => callback(fresh.transaction(storeName, txMode).objectStore(storeName)));
                }
                throw error;
            }
        });
}
